import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import './ResidentDetail.css';
import Sidebar from './Sidebar';
import BackButton from './BackButton';
import FieldsOfKnowledge from './FieldsOfKnowledge';
import TrendsSection from './TrendsSection';
import ClinicalExposureHeatmap from './ClinicalExposureHeatmap';

// KPI data by timeframe for each resident
interface KPIData {
  meanDocScore: number;
  medicalAdmissions: number;
  complexPatients: number;
  monthsInResidency: number;
  meanTimePerPatient: number;
}

interface TimeframeKPIs {
  '1 week': KPIData;
  '1 month': KPIData;
  '2 months': KPIData;
  '6 months': KPIData;
}

const residentKPIsByTimeframe: { [residentId: number]: TimeframeKPIs } = {
  1: { // Shani
    '1 week': { meanDocScore: 85, medicalAdmissions: 12, complexPatients: 4, monthsInResidency: 18, meanTimePerPatient: 44 },
    '1 month': { meanDocScore: 87, medicalAdmissions: 38, complexPatients: 11, monthsInResidency: 18, meanTimePerPatient: 42 },
    '2 months': { meanDocScore: 86, medicalAdmissions: 72, complexPatients: 22, monthsInResidency: 18, meanTimePerPatient: 43 },
    '6 months': { meanDocScore: 87, medicalAdmissions: 124, complexPatients: 38, monthsInResidency: 18, meanTimePerPatient: 42 },
  },
  2: { // Hillel
    '1 week': { meanDocScore: 94, medicalAdmissions: 15, complexPatients: 6, monthsInResidency: 24, meanTimePerPatient: 33 },
    '1 month': { meanDocScore: 93, medicalAdmissions: 48, complexPatients: 16, monthsInResidency: 24, meanTimePerPatient: 34 },
    '2 months': { meanDocScore: 92, medicalAdmissions: 94, complexPatients: 32, monthsInResidency: 24, meanTimePerPatient: 35 },
    '6 months': { meanDocScore: 92, medicalAdmissions: 156, complexPatients: 51, monthsInResidency: 24, meanTimePerPatient: 35 },
  },
  3: { // Maayan
    '1 week': { meanDocScore: 76, medicalAdmissions: 8, complexPatients: 2, monthsInResidency: 12, meanTimePerPatient: 52 },
    '1 month': { meanDocScore: 77, medicalAdmissions: 26, complexPatients: 7, monthsInResidency: 12, meanTimePerPatient: 50 },
    '2 months': { meanDocScore: 78, medicalAdmissions: 52, complexPatients: 14, monthsInResidency: 12, meanTimePerPatient: 49 },
    '6 months': { meanDocScore: 78, medicalAdmissions: 89, complexPatients: 22, monthsInResidency: 12, meanTimePerPatient: 48 },
  },
  4: { // Leehi
    '1 week': { meanDocScore: 96, medicalAdmissions: 18, complexPatients: 8, monthsInResidency: 30, meanTimePerPatient: 29 },
    '1 month': { meanDocScore: 95, medicalAdmissions: 54, complexPatients: 21, monthsInResidency: 30, meanTimePerPatient: 30 },
    '2 months': { meanDocScore: 94, medicalAdmissions: 106, complexPatients: 40, monthsInResidency: 30, meanTimePerPatient: 31 },
    '6 months': { meanDocScore: 94, medicalAdmissions: 178, complexPatients: 62, monthsInResidency: 30, meanTimePerPatient: 31 },
  },
  5: { // Sivan
    '1 week': { meanDocScore: 80, medicalAdmissions: 10, complexPatients: 3, monthsInResidency: 15, meanTimePerPatient: 47 },
    '1 month': { meanDocScore: 81, medicalAdmissions: 32, complexPatients: 9, monthsInResidency: 15, meanTimePerPatient: 46 },
    '2 months': { meanDocScore: 81, medicalAdmissions: 64, complexPatients: 18, monthsInResidency: 15, meanTimePerPatient: 45 },
    '6 months': { meanDocScore: 81, medicalAdmissions: 112, complexPatients: 29, monthsInResidency: 15, meanTimePerPatient: 45 },
  },
  6: { // Noa
    '1 week': { meanDocScore: 90, medicalAdmissions: 14, complexPatients: 5, monthsInResidency: 21, meanTimePerPatient: 36 },
    '1 month': { meanDocScore: 89, medicalAdmissions: 44, complexPatients: 15, monthsInResidency: 21, meanTimePerPatient: 37 },
    '2 months': { meanDocScore: 89, medicalAdmissions: 86, complexPatients: 29, monthsInResidency: 21, meanTimePerPatient: 38 },
    '6 months': { meanDocScore: 89, medicalAdmissions: 143, complexPatients: 47, monthsInResidency: 21, meanTimePerPatient: 38 },
  },
};

const residents = [
  {
    id: 1,
    name: 'Dr Shani Erez',
    image: '/assets/shani.jpg',
    section: 'Pediatrics',
    alertMessage: 'Last reviewed more than 3 months ago',
  },
  {
    id: 2,
    name: 'Dr Hillel Sztern',
    image: '/assets/hillel.jpg',
    section: 'Internal Medicine',
    alertMessage: null,
  },
  {
    id: 3,
    name: 'Dr Maayan',
    image: '/assets/maayan.jpg',
    section: 'Internal Medicine',
    alertMessage: 'Not enough clinical exposure in the last month',
  },
  {
    id: 4,
    name: 'Dr Leehi Pridan',
    image: '/assets/leehi.jpg',
    section: 'Internal Medicine',
    alertMessage: null,
  },
  {
    id: 5,
    name: 'Dr. Sivan Kijner',
    image: '/assets/sivan.jpg',
    section: 'Pediatrics',
    alertMessage: "Score trend stagnant for 3+ months",
  },
  {
    id: 6,
    name: 'Dr Noa Shohat',
    image: '/assets/noa.jpg',
    section: 'Internal Medicine',
    alertMessage: null,
  }
];

const ResidentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [globalTimeframe, setGlobalTimeframe] = useState<'1 week' | '1 month' | '2 months' | '6 months'>('1 month');
  
  const resident = residents.find(r => r.id === Number(id));
  const kpis = residentKPIsByTimeframe[Number(id)]?.[globalTimeframe];

  if (!resident || !kpis) {
    return <div>Resident not found</div>;
  }

  return (
    <div className="landing-page">
      <Sidebar />

      <div className="main-content">
        <BackButton />

        <div className="resident-detail-header">
          <img src={resident.image} alt={resident.name} className="resident-detail-image" />
          <div className="resident-detail-info">
            <div className="name-row">
              <h2>{resident.name}</h2>
              {resident.alertMessage && (
                <div className="resident-alert-badge">
                  <span className="alert-icon">⚠️</span>
                  <span className="alert-text">{resident.alertMessage}</span>
                </div>
              )}
            </div>
            <p>{resident.section}</p>
          </div>
          
          {/* Compact Timeframe Selector - in header */}
          <div className="header-timeframe-selector">
            <div className="timeframe-pills">
              {(['1 week', '1 month', '2 months', '6 months'] as const).map((tf) => (
                <button
                  key={tf}
                  className={`timeframe-pill ${globalTimeframe === tf ? 'active' : ''}`}
                  onClick={() => setGlobalTimeframe(tf)}
                >
                  {tf === '1 week' ? '1W' : tf === '1 month' ? '1M' : tf === '2 months' ? '2M' : '6M'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Widgets Section - Circular Design */}
        <div className="kpi-widgets">
          <div className="kpi-circle">
            <div className="kpi-circle-inner">
              <div className="kpi-icon">📅</div>
              <div className="kpi-value">{kpis.monthsInResidency}</div>
              <div className="kpi-unit">months</div>
            </div>
            <div className="kpi-label">Months in Residency</div>
          </div>
          <div className="kpi-circle">
            <div className="kpi-circle-inner">
              <div className="kpi-icon">📝</div>
              <div className="kpi-value">{kpis.meanDocScore}</div>
              <div className="kpi-unit">%</div>
            </div>
            <div className="kpi-label">Documentation Score</div>
          </div>
          <div className="kpi-circle">
            <div className="kpi-circle-inner">
              <div className="kpi-icon">🏥</div>
              <div className="kpi-value">{kpis.medicalAdmissions}</div>
            </div>
            <div className="kpi-label">Medical Admissions</div>
          </div>
          <div className="kpi-circle">
            <div className="kpi-circle-inner">
              <div className="kpi-icon">🩺</div>
              <div className="kpi-value">{kpis.complexPatients}</div>
            </div>
            <div className="kpi-label">Complex Patients</div>
          </div>
          <div className="kpi-circle">
            <div className="kpi-circle-inner">
              <div className="kpi-icon">⏱️</div>
              <div className="kpi-value">{kpis.meanTimePerPatient}</div>
              <div className="kpi-unit">min</div>
            </div>
            <div className="kpi-label">Time per Patient</div>
          </div>
        </div>

        {/* Clinical Exposure Heatmap - First Plot */}
        <ClinicalExposureHeatmap residentId={resident.id} globalTimeframe={globalTimeframe} />
        
        {/* Skill Trends Section with interactive chart and gauges */}
        <TrendsSection doctorName={resident.name} residentId={resident.id} globalTimeframe={globalTimeframe} />
        
        <div className="resident-performance">
          <h3>Performance by Clinical Field of Knowledge</h3>
          <div className="chart-container">
            <FieldsOfKnowledge doctorName={resident.name} timeFrame={globalTimeframe} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentDetail;
